// FILE: stratify_website/src/pages/TermsOfUsePage.jsx
import React from "react";
import { SITE } from "../config/site";
import { Meta } from "../layout/EducationMeta";
import GlassCard from "../components/ui/GlassCard";
import Link from "../components/Link";
import { LEGAL_SHARED, relatedLegalLinks } from "../config/legal";

const PAGE =
  "mx-auto w-full max-w-[110rem] px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28 border-t border-ink/8 pt-8 first:border-t-0 first:pt-0">
      <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-ink">{title}</h2>
      <div className="mt-4 space-y-4 text-sm md:text-[15px] leading-relaxed text-muted">
        {children}
      </div>
    </section>
  );
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex gap-3">
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-hint-strong" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function TermsOfUsePage() {
  const canonical = SITE.termsUrl;

  const termsSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Terms of Use — Stratify Backtesting",
    url: canonical,
    description:
      "Terms of Use and End User License Agreement for Stratify Backtesting.",
    publisher: {
      "@type": "Organization",
      name: SITE.legalName,
      url: SITE.url,
      email: SITE.supportEmail,
    },
  };

  return (
    <div className={`${PAGE} py-12 md:py-16`}>
      <Meta
        title="Terms of Use — Stratify Backtesting"
        description="Terms of Use and End User License Agreement for Stratify Backtesting."
        canonical={canonical}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(termsSchema) }}
      />

      <div className="max-w-4xl">
        <div className="inline-flex items-center rounded-full border border-hint/25 bg-hint/10 px-3 py-1 text-xs font-semibold tracking-wide text-hint-deep">
          Terms of Use / EULA
        </div>
        <h1 className="mt-5 text-3xl md:text-5xl font-semibold tracking-tight text-ink">
          Terms of Use
        </h1>
        <p className="mt-4 text-sm md:text-base leading-relaxed text-muted">
          These Terms of Use form an End User License Agreement between you and
          {" "}{SITE.legalName} for your use of {SITE.appName}, the Stratify
          Backtesting website, and related services.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-muted">
          {LEGAL_SHARED.billingDraftNote}
        </p>
      </div>

      <div className="mt-8 grid gap-5 lg:grid-cols-3">
        <GlassCard className="p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">
            Effective date
          </div>
          <div className="mt-2 text-lg font-semibold text-ink">May 26, 2026</div>
        </GlassCard>
        <GlassCard className="p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">
            Company
          </div>
          <div className="mt-2 text-lg font-semibold text-ink">{SITE.legalName}</div>
        </GlassCard>
        <GlassCard className="p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">
            Support
          </div>
          <Link to="/support" className="mt-2 inline-flex text-lg font-semibold text-hint-deep hover:text-ink transition">
            Contact support
          </Link>
        </GlassCard>
      </div>

      <main className="mt-10">
        <GlassCard className="p-6 md:p-8">
          <div className="space-y-8">
            <Section id="introduction" title="1. Introduction">
              <p>
                “Stratify,” “we,” “our,” and “us” refer to {SITE.legalName}.
                “User” and “you” refer to the person using the app, website, or
                related services.
              </p>
              <p>
                By downloading, installing, accessing, or using Stratify, you agree
                to these Terms. If you do not agree, do not install or use the app
                or website. Stratify is intended for users who are at least 13 years
                old.
              </p>
            </Section>

            <Section id="license" title="2. License grant">
              <p>
                We grant you a limited, non-exclusive, non-transferable, revocable
                license to download, install, and use Stratify solely for personal,
                non-commercial, educational, research, simulation, and paper trading
                purposes, subject to these Terms.
              </p>
            </Section>

            <Section id="ownership" title="3. Ownership and intellectual property">
              <p>
                The app, website, content, code, design, strategy tooling,
                calculations, analytics, trademarks, and related intellectual
                property are owned by {SITE.legalName} or our licensors. Your use
                of Stratify does not grant you ownership rights. All rights not
                expressly granted are reserved.
              </p>
            </Section>

            <Section id="prohibited-use" title="4. Prohibited commercial use and resale">
              <p>You may not use, copy, modify, distribute, resell, or commercialize Stratify or any part of its code, design, algorithms, outputs, or content for unauthorized commercial purposes.</p>
              <p>This includes, but is not limited to:</p>
              <BulletList
                items={[
                  "Selling, licensing, or redistributing the app or a derivative work based on it",
                  "Using Stratify code, algorithms, data outputs, or analytics in a competing product or service",
                  "Using Stratify outputs in a paid advisory product, commercial trading service, or managed trading offering",
                  "Attempting to reverse engineer, scrape, copy, or misuse app functionality beyond permitted use",
                ]}
              />
            </Section>

            <Section id="accounts" title="5. User accounts and security">
              <p>
                Some Stratify features require an account. You agree to provide
                accurate information, keep your login credentials confidential, and
                accept responsibility for activity under your account. We are not
                responsible for unauthorized access caused by your failure to secure
                your credentials.
              </p>
            </Section>

            <Section id="privacy" title="6. Data collection and privacy">
              <p>
                We collect and process information as described in our Privacy Policy.
                For details about what we collect, how we use it, third-party services,
                retention, and deletion requests, review our Privacy Policy.
              </p>
              <Link
                to="/privacy-policy"
                className="inline-flex items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-3 text-sm font-semibold text-ink/80 hover:bg-bg1 hover:text-ink transition"
              >
                View Privacy Policy →
              </Link>
            </Section>

            <Section id="educational-use" title="7. Educational use only — no financial advice">
              <p>
                Stratify is for educational, research, simulation, paper trading, and
                backtesting purposes only. Stratify does not provide financial,
                investment, legal, tax, trading, or brokerage advice.
              </p>
              <p>
                No feature, result, strategy, chart, indicator, alert, signal label,
                buy/sell status, notification, backtest, or simulation guarantees any
                specific financial outcome. All trading and investment decisions are
                your responsibility and are made at your own risk.
              </p>
              <p>
                Stratify is not a broker-dealer, brokerage platform, investment
                advisor, financial advisor, exchange, or execution venue.
              </p>
              <p>{LEGAL_SHARED.noLiveTrading}</p>
              <p>{LEGAL_SHARED.simulation}</p>
            </Section>

            <Section id="website-billing" title="7A. Subscriptions and website billing (founder draft)">
              <p>{LEGAL_SHARED.sellers}</p>
              <p>
                Checkout or the App Store identifies the plan, price, currency, and
                billing period before you pay. Subscriptions renew automatically until
                cancelled. Cancellation takes effect at the end of the paid period.
                Access continues until that date.
              </p>
              <p>{LEGAL_SHARED.marketing}</p>
              <p>
                Deleting the app, signing out, or requesting account deletion does not
                automatically cancel a Stripe or Apple subscription. Cancel billing
                separately as described in the Subscription Terms.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/subscription-terms"
                  className="inline-flex items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-3 text-sm font-semibold text-ink/80 hover:bg-bg1 hover:text-ink transition"
                >
                  Subscription Terms →
                </Link>
                <Link
                  to="/refund-policy"
                  className="inline-flex items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-3 text-sm font-semibold text-ink/80 hover:bg-bg1 hover:text-ink transition"
                >
                  Refund Policy →
                </Link>
              </div>
            </Section>

            <Section id="data-accuracy" title="8. Data accuracy, code errors, and notification delivery">
              <p>
                While we strive to make Stratify reliable and useful, you acknowledge
                that bugs, backend code errors, incorrect calculations, delayed market
                data, missing market data, inaccurate third-party data, outages, and
                unexpected behavior may occur.
              </p>
              <p>
                Stock prices, market information, and other market-related inputs may
                be sourced from third-party providers. We do not control or guarantee
                the availability, accuracy, completeness, or timeliness of third-party
                data.
              </p>
              <p>
                Notifications may be delayed, filtered, blocked, or fail to deliver
                due to device settings, network conditions, Apple services, Firebase
                services, server issues, or third-party service performance.
              </p>
            </Section>

            <Section id="warranty" title="9. No warranty">
              <p>
                Stratify is provided “AS IS” and “AS AVAILABLE,” without warranties
                of any kind, whether express, implied, or statutory. We do not warrant
                that Stratify will be uninterrupted, error-free, secure, accurate, or
                suitable for your specific needs.
              </p>
            </Section>

            <Section id="liability" title="10. Limitation of liability">
              <p>
                To the maximum extent permitted by law, {SITE.legalName} will not be
                liable for indirect, incidental, special, consequential, exemplary, or
                punitive damages, including loss of profits, revenue, data, trading
                opportunities, investment value, or business opportunities arising from
                your use of or inability to use Stratify.
              </p>
              <p>
                Your sole and exclusive remedy for dissatisfaction with Stratify is to
                stop using the app and delete your account.
              </p>
            </Section>

            <Section id="termination" title="11. Account termination">
              <p>
                We may suspend or terminate access to Stratify if you violate these
                Terms, misuse the app, attempt to disrupt services, or engage in
                unauthorized or unlawful activity. You may stop using Stratify at any
                time and may request account deletion through support.
              </p>
            </Section>

            <Section id="assignment" title="12. No transferability or assignment">
              <p>
                You may not assign, transfer, sublicense, or delegate your rights or
                obligations under these Terms without our prior written consent. Any
                attempted assignment in violation of this section is null and void.
              </p>
            </Section>

            <Section id="consumer-rights" title="13. Consumer rights notice">
              <p>
                Some jurisdictions do not allow the exclusion of certain warranties or
                limitation of certain damages. If those laws apply to you, some parts
                of these Terms may not apply, and you may have additional rights.
              </p>
            </Section>

            <Section id="changes" title="14. Changes to these Terms">
              <p>
                We may update these Terms from time to time. Updated Terms will be
                posted on this page. Continued use of Stratify after changes take
                effect constitutes acceptance of the updated Terms.
              </p>
            </Section>

            <Section id="law" title="15. Governing law">
              <p>
                These Terms are governed by the laws of the Province of Ontario,
                Canada, where applicable, without regard to conflict of law principles.
                Additional consumer protection laws may apply depending on your location.
              </p>
            </Section>

            <Section id="contact" title="16. Contact">
              <p>For questions about these Terms, contact us at:</p>
              <div className="rounded-2xl border border-ink/8 bg-bg1 p-5">
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-muted">
                  Email
                </div>
                <a
                  href={`mailto:${SITE.supportEmail}`}
                  className="mt-2 inline-flex break-all text-lg font-semibold text-hint-deep hover:text-ink transition"
                >
                  {SITE.supportEmail}
                </a>
                <div className="mt-4 text-xs uppercase tracking-[0.16em] text-muted">
                  Company
                </div>
                <div className="mt-1 font-semibold text-ink">{SITE.legalName}</div>
              </div>
            </Section>
          </div>
        </GlassCard>
        <div className="mt-5 flex flex-wrap gap-3">
          {relatedLegalLinks("/terms-of-use").map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="inline-flex items-center justify-center rounded-xl border border-ink/8 bg-bg1 px-4 py-3 text-sm font-semibold text-ink/80 hover:bg-bg1 hover:text-ink transition"
            >
              {item.label} →
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
